import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PenLine, Plus, Trash2, Gem } from "lucide-react";

const GENRES = ["Romance", "Fantasy", "Dark Romance", "Paranormal", "Drama", "Mystery", "Sci-Fi", "Horror", "Slice of Life"];
const L = "font-mono text-[10px] uppercase tracking-widest text-amber-400";

export default function WriteStory() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", genre: "Romance", tagline: "", about_author: "", cover_file_id: "" });
  const [chapters, setChapters] = useState([{ title: "Chapter 1", content: "" }]);
  const [arts, setArts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) api.get("/me/assets", { params: { kind: "user_art" } }).then((r) => setArts(r.data || [])).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold mb-3">Sign in to share your story</h1>
        <Link to="/login?next=/write" data-testid="write-signin" className="inline-flex items-center justify-center rounded-full bg-amber-500 text-black font-semibold px-7 h-12">Sign in to continue</Link>
      </div>
    );
  }

  const isSub = user.is_admin || (user.subscription_status && user.subscription_status !== "free");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));
  const setCh = (i, k, v) => setChapters((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const submit = async () => {
    if (!form.title.trim() || !form.tagline.trim()) { toast.error("Title and tagline are required"); return; }
    if (chapters.some((c) => c.content.trim().length < 200)) { toast.error("Each chapter needs at least 200 characters"); return; }
    setBusy(true);
    try {
      const body = { ...form, cover_file_id: form.cover_file_id || null, chapters, contest_id: new URLSearchParams(window.location.search).get("contest") || null };
      const { data } = await api.post("/stories/write", body);
      toast.success(data.story.approval_status === "pending" ? "Submitted for review — we'll let you know on your Dashboard" : "Published!");
      await refresh?.();
      nav(`/story/${data.story.story_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2 flex items-center gap-2"><PenLine className="h-3 w-3" /> Human-written · {isSub ? "Free with your subscription" : "20 gems · free for subscribers"}</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">Write it yourself.</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">Your words, your name. Approved stories appear on <Link to="/human-stories" className="text-rose-300 underline">Human Hearts</Link> with your author profile — and earn you 50 gems.</p>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-10 space-y-5">
        <div className="grid sm:grid-cols-[1fr_200px] gap-4">
          <div><Label className={L}>Title</Label><Input data-testid="write-title" value={form.title} onChange={set("title")} maxLength={120} className="mt-1 bg-[#07080A] border-white/10" /></div>
          <div>
            <Label className={L}>Genre</Label>
            <Select value={form.genre} onValueChange={set("genre")}>
              <SelectTrigger data-testid="write-genre" className="mt-1 bg-[#07080A] border-white/10 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label className={L}>Tagline (one line hook)</Label><Input data-testid="write-tagline" value={form.tagline} onChange={set("tagline")} maxLength={240} className="mt-1 bg-[#07080A] border-white/10" /></div>
        <div><Label className={L}>About you (shown on your story card)</Label><Input data-testid="write-about" value={form.about_author} onChange={set("about_author")} maxLength={400} placeholder="e.g. Night-shift nurse who writes slow-burn romance" className="mt-1 bg-[#07080A] border-white/10" /></div>
        <div>
          <Label className={L}>Cover</Label>
          <Select value={form.cover_file_id || "ai"} onValueChange={(v) => setForm((f) => ({ ...f, cover_file_id: v === "ai" ? "" : v }))}>
            <SelectTrigger data-testid="write-cover" className="mt-1 bg-[#07080A] border-white/10 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ai">Let AI paint a cover</SelectItem>
              {arts.map((a) => <SelectItem key={a.file_id} value={a.file_id}>{a.title || "My painting"}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.cover_file_id && (() => { const a = arts.find((x) => x.file_id === form.cover_file_id); return a ? <img src={fileUrl(a.url)} alt="" className="mt-2 h-40 rounded-xl object-cover border border-white/10" /> : null; })()}
        </div>

        {chapters.map((c, i) => (
          <div key={i} data-testid={`write-chapter-${i}`} className="rounded-2xl border border-white/10 bg-[#07080A]/60 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Input data-testid={`write-chapter-title-${i}`} value={c.title} onChange={(e) => setCh(i, "title", e.target.value)} maxLength={120} className="bg-[#07080A] border-white/10" />
              {chapters.length > 1 && (
                <button data-testid={`write-chapter-remove-${i}`} onClick={() => setChapters((cs) => cs.filter((_, j) => j !== i))} className="p-2 text-rose-300 hover:text-rose-200"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            <Textarea data-testid={`write-chapter-content-${i}`} value={c.content} onChange={(e) => setCh(i, "content", e.target.value)} rows={10} placeholder="Write your chapter…" className="bg-[#07080A] border-white/10 font-serif text-base leading-relaxed" />
            <div className="text-[11px] font-mono text-slate-500 text-right">{c.content.trim().split(/\s+/).filter(Boolean).length} words</div>
          </div>
        ))}
        {chapters.length < 20 && (
          <Button data-testid="write-add-chapter" type="button" variant="outline" onClick={() => setChapters((cs) => [...cs, { title: `Chapter ${cs.length + 1}`, content: "" }])} className="rounded-full border-white/15 text-slate-300">
            <Plus className="h-4 w-4 mr-1" /> Add chapter
          </Button>
        )}

        <Button data-testid="write-submit" onClick={submit} disabled={busy} className="w-full h-14 rounded-full bg-rose-500 hover:bg-rose-400 text-black font-bold text-base">
          {busy ? "Submitting…" : <>Submit for review — {isSub ? "free" : <span className="inline-flex items-center gap-1 ml-1"><Gem className="h-4 w-4" /> 20</span>}</>}
        </Button>
        <p className="text-center text-xs text-slate-500">Every story is reviewed by our editors before it goes public · gems refunded if declined</p>
      </div>
    </div>
  );
}
